import { useEffect, useState } from 'react';
import classNames from 'classnames';
import { prefixClass } from 'lib/utils';
import { Dropdown } from '../../../../../../Common/Dropdown';
import IconButton from '../../../../../../Common/IconButton';
import { UserSearch } from '../../../../../../Common/UserSearch';
import { SimpleDatePicker } from '../../../../../ConversationPanel/components/Header/components/DueDateButton/components/SimpleDatePicker';
import type { UserSearchUser, SpecialItemType } from '../../../../../../Common';
import type { QueueView } from '@hone-automation/common';
import {
  IconAssignmentNew,
  IconNavCalendar,
  IconValidateNew,
  IconPromote,
  IconReopen,
  IconFolderAdd,
  IconFolderDelete,
  IconFolderMove,
  IconFolder,
} from 'components/Icons';
import './MultiSelectBar.scss';

/** Group item for dropdown */
export interface GroupItem {
  id: string;
  name: string;
}

export interface MultiSelectBarProps {
  /** Number of selected items */
  selectedCount: number;
  /** Total number of items in the list */
  totalCount: number;
  /** Whether the bar is visible */
  isVisible: boolean;
  /** Current active view - used to determine Block button behavior */
  activeView?: QueueView;
  /** Mobile layout variant */
  mobile?: boolean;
  /** Select all items */
  onSelectAll: () => void;
  /** Deselect all items */
  onDeselectAll: () => void;
  /** Assign selected items to a user */
  onAssign: (userId: string | null) => void;
  /** Set due date for selected items */
  onSetDueDate: (date: Date | null) => void;
  /** Resolve selected items (set status to Closed) */
  onResolve: () => void;
  /** Reopen selected items (set status to Open) */
  onReopen: () => void;
  /** Toggle blocker priority for selected items */
  onToggleBlocker: (isBlocker: boolean) => void;
  /** Remove blocker priority for selected items */
  onRemoveToggleBlock?: () => void;
  /** Whether all selected items are already blockers */
  selectedAreBlockers?: boolean;
  /** Users available for assignment */
  assigneeUsers?: any[];
  /** Current user for "Assign to me" option */
  currentUser?: { userId: string; name: string };
  /** Available groups for "Add to group" dropdown */
  groups?: GroupItem[];
  /** Current group ID when viewing a group (enables "Remove from group") */
  currentGroupId?: string | null;
  /** Create a new group with selected items */
  onCreateGroup?: () => void;
  /** Add selected items to an existing group */
  onAddToGroup?: (groupId: string) => void;
  /** Remove selected items from current group */
  onRemoveFromGroup?: () => void;
}

/**
 * MultiSelectBar - Floating action bar that appears when multiple items are selected.
 * Provides bulk actions for selected requests.
 */
export const MultiSelectBar = ({
  selectedCount,
  totalCount,
  isVisible,
  activeView,
  mobile,
  onSelectAll,
  onDeselectAll,
  onAssign,
  onSetDueDate,
  onResolve,
  onReopen,
  onToggleBlocker,
  onRemoveToggleBlock,
  selectedAreBlockers = false,
  assigneeUsers,
  currentUser,
  groups = [],
  currentGroupId,
  onCreateGroup,
  onAddToGroup,
  onRemoveFromGroup,
}: MultiSelectBarProps) => {
  const prefix = prefixClass('ksq2-multi-select-bar');

  // Dropdown states
  const [assignOpen, setAssignOpen] = useState(false);
  const [dueDateOpen, setDueDateOpen] = useState(false);
  const [selectedDueDate, setSelectedDueDate] = useState<Date | null>(null);
  const [addToGroupOpen, setAddToGroupOpen] = useState(false);

  const allSelected = selectedCount === totalCount && totalCount > 0;

  const isClosedView = activeView === 'Completed';

  // Reset due date when dropdown closes
  useEffect(() => {
    if (!dueDateOpen) {
      setSelectedDueDate(null);
    }
  }, [dueDateOpen]);

  const handleUserSelect = (user: UserSearchUser) => {
    onAssign(user.userId);
    setAssignOpen(false);
  };

  const handleSpecialSelect = (type: SpecialItemType, userId?: string) => {
    if (type === 'unassigned') {
      onAssign(null);
    } else if (type === 'assign-to-me' && userId) {
      onAssign(userId);
    }
    setAssignOpen(false);
  };

  const handleDueDateConfirm = () => {
    onSetDueDate(selectedDueDate);
    setDueDateOpen(false);
    setSelectedDueDate(null);
  };

  const handleDueDateClear = () => {
    onSetDueDate(null);
    setDueDateOpen(false);
    setSelectedDueDate(null);
  };

  const handleBlockerClick = () => {
    if (selectedAreBlockers && onRemoveToggleBlock) {
      onRemoveToggleBlock();
    } else {
      onToggleBlocker(!selectedAreBlockers);
    }
  };

  return (
    <div className={`${prefix()}${isVisible ? ` ${prefix('--visible')}` : ''}`}>
      <div className={prefix('__content')}>
        {/* Left: Count + All/Clear actions */}
        <div className={prefix('__selection-info')}>
          <div className={prefix('__count')}>({selectedCount})</div>
          <button
            type="button"
            className={classNames(prefix('__action-btn'), { disabled: allSelected })}
            onClick={onSelectAll}
            disabled={allSelected}
          >
            All
          </button>
          <span>|</span>
          <button type="button" className={prefix('__action-btn')} onClick={onDeselectAll}>
            Clear
          </button>
        </div>

        {/* Right: All action buttons */}
        <div className={prefix('__actions-container')}>
          {/* Bulk action buttons */}
          <div className={classNames(prefix('__bulk-actions'), { mobile })}>
            {/* Assign */}
            <Dropdown
              trigger={
                <IconButton icon={<IconAssignmentNew />} onClick={() => {}} size="medium" tooltip="Assign selected" />
              }
              position="bottom"
              alignment="end"
              isOpen={assignOpen}
              onOpenChange={setAssignOpen}
              closeOnItemClick={false}
              className="ksq2-multi-select-assign-dropdown"
              usePortal={mobile}
            >
              <UserSearch
                users={assigneeUsers}
                placeholder="Search team members..."
                onUserSelect={handleUserSelect}
                onSpecialSelect={handleSpecialSelect}
                onClose={() => setAssignOpen(false)}
                isOpen={assignOpen}
                showSpecialItems
                currentUser={currentUser}
              />
            </Dropdown>

            {/* Due Date */}
            {!isClosedView && (
              <Dropdown
                trigger={
                  <IconButton icon={<IconNavCalendar />} onClick={() => {}} size="medium" tooltip="Set due date" />
                }
                position="bottom"
                alignment="end"
                isOpen={dueDateOpen}
                onOpenChange={setDueDateOpen}
                closeOnItemClick={false}
                className="ksq2-multi-select-duedate-dropdown"
                usePortal={mobile}
              >
                <SimpleDatePicker
                  selectedDate={selectedDueDate}
                  onDateChange={setSelectedDueDate}
                  onConfirm={handleDueDateConfirm}
                  onClear={handleDueDateClear}
                />
              </Dropdown>
            )}

            {/* Block/Unblock */}
            {!isClosedView && (
              <IconButton
                icon={<IconPromote />}
                onClick={handleBlockerClick}
                size="medium"
                tooltip={selectedAreBlockers ? 'Remove blocker' : 'Mark as blocker'}
                selected={selectedAreBlockers}
                type="switch"
                className="blocker-toggle"
              />
            )}

            {/* Resolve / Reopen */}
            {!isClosedView && (
              <IconButton icon={<IconValidateNew />} onClick={onResolve} size="medium" tooltip="Resolve selected" />
            )}
            {isClosedView && (
              <IconButton icon={<IconReopen />} onClick={onReopen} size="medium" tooltip="Reopen selected" />
            )}
          </div>

          {/* Separator between bulk actions and group actions */}
          <div className={prefix('__separator')} />

          {/* Group operations */}
          <div className={prefix('__group-actions')}>
            {/* Create Group - Hide when viewing a group (nested groups not supported) */}
            {onCreateGroup && activeView !== 'Groups' && (
              <IconButton icon={<IconFolder />} onClick={onCreateGroup} size="medium" tooltip="Create group" />
            )}

            {/* Add to Group - Hide when viewing a group */}
            {onAddToGroup && activeView !== 'Groups' && (
              <Dropdown
                trigger={
                  <IconButton icon={<IconFolderAdd />} onClick={() => {}} size="medium" tooltip="Add to group" />
                }
                position="bottom"
                alignment="end"
                isOpen={addToGroupOpen}
                onOpenChange={setAddToGroupOpen}
                items={
                  groups.length > 0
                    ? groups
                        .filter(group => group.id !== currentGroupId) // Exclude current group
                        .map(group => ({
                          id: group.id,
                          label: group.name,
                          onClick: () => {
                            onAddToGroup?.(group.id);
                            setAddToGroupOpen(false);
                          },
                        }))
                    : [{ id: 'no-groups', label: 'No groups yet', onClick: () => {} }]
                }
                className="ksq2-multi-select-group-dropdown"
              />
            )}

            {/* Move to another group - Only show when viewing a group */}
            {onAddToGroup && activeView === 'Groups' && (
              <Dropdown
                trigger={
                  <IconButton
                    icon={<IconFolderMove />}
                    onClick={() => {}}
                    size="medium"
                    tooltip="Move to another group"
                  />
                }
                position="bottom"
                alignment="end"
                isOpen={addToGroupOpen}
                onOpenChange={setAddToGroupOpen}
                items={
                  groups.length > 0
                    ? groups
                        .filter(group => group.id !== currentGroupId) // Exclude current group
                        .map(group => ({
                          id: group.id,
                          label: group.name,
                          onClick: () => {
                            onAddToGroup?.(group.id);
                            setAddToGroupOpen(false);
                          },
                        }))
                    : [{ id: 'no-groups', label: 'No groups yet', onClick: () => {} }]
                }
                className="ksq2-multi-select-group-dropdown"
              />
            )}

            {/* Remove from Group - Only show when viewing a group */}
            {currentGroupId && activeView === 'Groups' && onRemoveFromGroup && (
              <IconButton
                icon={<IconFolderDelete />}
                onClick={onRemoveFromGroup}
                size="medium"
                tooltip="Remove from group"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
